import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface RateLimitResult {
  allowed: boolean;
  attempts: number;
  remaining: number;
  resetAt?: string;
  blockedUntil?: string;
  message?: string;
}

interface UseRateLimitReturn {
  checkRateLimit: (endpoint: string, maxAttempts?: number, windowMinutes?: number) => Promise<RateLimitResult>;
  resetRateLimit: (endpoint: string) => Promise<void>;
  isLoading: boolean;
}

export const useRateLimit = (): UseRateLimitReturn => {
  const [isLoading, setIsLoading] = useState(false);

  const getIdentifier = useCallback(() => {
    // Use a combination of factors to identify the user
    // In a real app, you might want to use IP address from server-side
    const userAgent = navigator.userAgent;
    const language = navigator.language;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Create a simple hash-like identifier
    return btoa(`${userAgent}-${language}-${timezone}`).substring(0, 32);
  }, []);

  const checkRateLimit = useCallback(async (
    endpoint: string,
    maxAttempts: number = 5,
    windowMinutes: number = 15
  ): Promise<RateLimitResult> => {
    setIsLoading(true);
    
    try {
      const identifier = getIdentifier();
      
      const { data, error } = await supabase.rpc('check_rate_limit', {
        p_identifier: identifier,
        p_endpoint: endpoint,
        p_max_attempts: maxAttempts,
        p_window_minutes: windowMinutes
      });

      if (error) {
        console.error('Rate limit check failed:', error);
        // Allow request if rate limiting fails
        return {
          allowed: true,
          attempts: 0,
          remaining: maxAttempts,
          message: 'Rate limit check failed, allowing request'
        };
      }

      return data as RateLimitResult;
    } catch (error) {
      console.error('Rate limit error:', error);
      // Allow request if rate limiting fails
      return {
        allowed: true,
        attempts: 0,
        remaining: maxAttempts,
        message: 'Rate limit check failed, allowing request'
      };
    } finally {
      setIsLoading(false);
    }
  }, [getIdentifier]);

  const resetRateLimit = useCallback(async (endpoint: string): Promise<void> => {
    try {
      const identifier = getIdentifier();
      
      await supabase.rpc('reset_rate_limit', {
        p_identifier: identifier,
        p_endpoint: endpoint
      });
    } catch (error) {
      console.error('Rate limit reset failed:', error);
    }
  }, [getIdentifier]);

  return {
    checkRateLimit,
    resetRateLimit,
    isLoading
  };
};
